import {useGameServerConnection, usePlayers} from '@glark-newco/game-library';
import type {IPointData} from '@pixi/core';
import {FederatedPointerEvent} from '@pixi/events';
import {useApp} from '@pixi/react';
import {useCallback} from 'react';
import {Tile, useGameBoard} from './signlinesBoardSlice';
import {useUIStore} from './useSignlinesUIStore';
import {PlayTileEvent, SignlinesPayload} from '../SignlinesPayloadTypes';
import {colIndexFromPoint, cols, rowIndexFromPoint, rows, TILE_HEIGHT, TILE_WIDTH} from '../utils/GameBoardUtils';


export const useTileInteraction = () => {
    // ----- selectors -------------------------------------------------
    const isDragging = useUIStore(state => state.isDragging);
    const tile = useUIStore(state => state.tile);
    const isZoom = useUIStore(state => state.isZoom);
    const offset = useUIStore(state => state.offset);
    const position = useUIStore(state => state.position);

    // ----- actions ---------------------------------------------------
    const startDrag = useUIStore(state => state.startDrag);
    const updatePosition = useUIStore(state => state.updatePosition);
    const endDrag = useUIStore(state => state.endDrag);
    const setZoom = useUIStore(state => state.setZoom);

    // ----- Pixi app (needed for coordinate conversion) ----------
    const app = useApp();
    const {publishPlayerAction} = useGameServerConnection<SignlinesPayload>();
    const gameBoard = useGameBoard();
    const {localPlayer} = usePlayers();

    // ----- callbacks that keep the original public API ---------------
    const startDragging = useCallback((t: Tile, off: IPointData) => {
        const pointer = app.renderer.events.pointer;
        const cursor: IPointData = {x: 0, y: 0};
        app.renderer.events.mapPositionToPoint(cursor, pointer.x, pointer.y);
        startDrag(t, off, cursor);
    }, [app, startDrag]);

    const handleDragEnd = useCallback((event: FederatedPointerEvent) => {
        const colIndex = colIndexFromPoint(event.global);
        const rowIndex = rowIndexFromPoint(event.global);

        if (isDragging && colIndex >= 0 && rowIndex >= 0) {
            const targetLocation = `${cols[colIndex]}${rows[rowIndex]}`;
            const cell = gameBoard.find(c => c.location === targetLocation);
            if (cell && !cell.tileId) {
                publishPlayerAction({
                    type: 'playTileEvent',
                    playerId: localPlayer?.playerId,
                    tileId: tile?.tileId,
                    positionId: cell.location,
                } as PlayTileEvent);
            }
        }
        endDrag();
    }, [isDragging, gameBoard, tile, localPlayer, publishPlayerAction, endDrag]);

    const handleMouseMove = useCallback((event: FederatedPointerEvent) => {
        if (!isDragging) return;

        // Convert the raw pointer coordinates to stage coordinates.
        const cursor: IPointData = {x: 0, y: 0};
        app.renderer.events.mapPositionToPoint(cursor, event.x, event.y);

        //   xMin = offset.x + 400                // left margin (carousel)
        //   xMax = 1500 - TILE_WIDTH + offset.x  // right margin (chat)
        //   yMin = offset.y                      // top of the board
        //   yMax = 1080 - TILE_HEIGHT + offset.y // bottom of the board
        // If the cursor tries to move outside those limits we clamp it.
        const off = offset ?? {x: 0, y: 0}; // offset is always defined while dragging
        const xMin = off.x + 400;
        const xMax = 1500 - TILE_WIDTH + off.x;
        const yMin = off.y;
        const yMax = 1080 - TILE_HEIGHT + off.y;

        const clamped = {
            x: (cursor.x < xMin) ? xMin : (cursor.x > xMax) ? xMax : cursor.x,
            y: (cursor.y < yMin) ? yMin : (cursor.y > yMax) ? yMax : cursor.y,
        };
        updatePosition(clamped);
    }, [isDragging, app, offset, updatePosition]);

    const cancelDrag = useCallback(() => endDrag(), [endDrag]);

    return {
        cancelDrag,
        isDragging,
        isZoom,
        setZoom,
        tile,
        offset,
        startDragging,
        handleDragEnd,
        position,
        handleMouseMove,
    };
};
